import type { CustomerDocument } from '@protoss/shared';
import * as React from 'react';
import { customersApi } from '../services/customersApi';

export type DocumentAddDraft =
  | { kind: 'paste'; title: string; text: string }
  | { kind: 'upload'; title: string; file: File };

interface UseCustomerDocumentsResult {
  documents: CustomerDocument[];
  isLoading: boolean;
  addDocument: (draft: DocumentAddDraft) => Promise<void>;
  deleteDocument: (docUuid: string) => Promise<void>;
  refetch: () => void;
}

export function useCustomerDocuments(customerUuid: string): UseCustomerDocumentsResult {
  const [documents, setDocuments] = React.useState<CustomerDocument[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const refetch = React.useCallback(() => {
    setIsLoading(true);
    customersApi
      .listDocuments(customerUuid)
      .then(({ documents: docs }) => setDocuments(docs))
      .catch((err) => console.error('[useCustomerDocuments] failed to load documents', err))
      .finally(() => setIsLoading(false));
  }, [customerUuid]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  const addDocument = React.useCallback(
    async (draft: DocumentAddDraft) => {
      let created: CustomerDocument;
      if (draft.kind === 'paste') {
        const res = await customersApi.createPasteDocument(
          customerUuid,
          draft.text,
          draft.title || undefined
        );
        created = res.document;
      } else {
        const res = await customersApi.createUploadDocument(
          customerUuid,
          draft.file,
          draft.title || undefined
        );
        created = res.document;
      }
      // Immediately prepend the new doc (with chunkStatus:'pending') so the
      // processing indicator is visible before refetch() resolves.
      setDocuments((prev) => [created, ...prev]);
      refetch();
    },
    [customerUuid, refetch]
  );

  const deleteDocument = React.useCallback(
    async (docUuid: string) => {
      await customersApi.deleteDocument(customerUuid, docUuid);
      refetch();
    },
    [customerUuid, refetch]
  );

  return { documents, isLoading, addDocument, deleteDocument, refetch };
}
